/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  TRANS: 'trans',
  TRANS_FAILED: 'transFailed',
  FAILED: 'failed',
  SUCCESS: 'success'
};

const MODE = {
  NORMAL: 'normal',
  CHECK: 'check'
};

export default {
  name: 'requirementSpECM2',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: '',
      currentMode: '',
      systemId: '',
      modal_special_requirement_ECM2: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.TRANS_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ECM2 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ECM2 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 現在モード読み出し
     */
    async currentModeRead() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // 現在モード読み出し(要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read_mode',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              if (response.data.user_data.value === "40") {
                // 現在のモード：チェックモード
                this.displayInfo = 'individual.label_normal_mode_transition_ECM2';
                this.currentMode = MODE.CHECK;
              } else {
                // 現在のモード：ノーマルモード
                this.displayInfo = 'individual.label_check_mode_transition_ECM2';
                this.currentMode = MODE.NORMAL;
              }
              this.phase = PHASE.TRANS;
              break;
            default:
              await this.read();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * データ読出し
     */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // データ読出し(要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              if (response.data.user_data.value === "40") {
                // 現在のモード：チェックモード
                this.displayInfo = 'individual.label_normal_mode_transition_ECM2';
                this.currentMode = MODE.CHECK;
              } else {
                // 現在のモード：ノーマルモード
                this.displayInfo = 'individual.label_check_mode_transition_ECM2';
                this.currentMode = MODE.NORMAL;
              }
              this.phase = PHASE.TRANS;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * NonDefaultSession遷移 or DefaultSession遷移
     */
    async sessionTransition() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // Session遷移(要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'transition',
            transMode: this.currentMode === MODE.CHECK ? MODE.NORMAL : MODE.CHECK,
            is_special: true
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 肯定応答 または 否定応答の場合はBackendがCompletedを返し、遷移成功を表示
              if (this.currentMode === MODE.CHECK) {
                this.displayInfo = 'individual.label_normal_mode_transition_success_ECM2';
              } else {
                this.displayInfo = 'individual.label_check_mode_transition_success_ECM2';
              }
              this.phase = PHASE.SUCCESS;
              break;
            default:
              // 無応答であれば遷移失敗を表示
              if (this.currentMode === MODE.CHECK) {
                this.displayInfo = 'individual.label_normal_mode_transition_error_ECM2';
              } else {
                this.displayInfo = 'individual.label_check_mode_transition_error_ECM2';
              }
              this.phase = PHASE.TRANS_FAILED;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** モード遷移確認画面 OKボタン押下イベント */
    async sessionProcessEvent() {
      await this.sessionTransition();
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    await this.currentModeRead();
  },
};